// DEPENDENCIES
import React, { useState, useEffect, useContext } from "react";
import dayjs from "dayjs";

// CSS
import "./BuildingElements.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// API IMPORTS
import { getAPICall, postAPICall } from "../../config/apiCalls";

// CONTEXT
import { UserContext } from "../../context/UserContext";

// MUI COMPONENTS
import { Button, CircularProgress, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";

// CUSTOM COMPONENTS
import TanStackTable from "../Misc/Table/TanStackTable";
import J2NoResultsFound from "../Misc/J2NoResultsFound/J2NoResultsFound";
import BuildingElementsEditAdd from "./BuildingElementsEditAdd/BuildingElementsEditAdd";

// CONSTANTS
import { defaultSort, notApplicable } from "../Misc/Table/TanStackTableConstants";

// FUNCTIONAL COMPONENT
const BuildingElements = (props) => {
	// MSAL
	const { instance, accounts } = useMsal();

	// CONTEXT
	const employeeID = useContext(UserContext);

	// STATES
	const [buildingElements, setBuildingElements] = useState([]);
	const [columns, setColumns] = useState([]);
	const [rows, setRows] = useState([]);

	// Feedback
	const [isLoading, setIsLoading] = useState(false);

	// Dialogs
	const [editAddOpen, setEditAddOpen] = useState(false);
	const [editing, setEditing] = useState(false);
	const [recordToEdit, setRecordToEdit] = useState(null);

	// USE EFFECT
	useEffect(() => {
		fetchData();
		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: "Building Elements",
			employeeID: employeeID,
			date_time: dayjs().format("MMMM D, YYYY h:mm:ss A"),
			notes: "Accessed the Building Elements page",
		});
	}, []);

	useEffect(() => {
		if (buildingElements.length > 0) {
			buildColumns();
			buildRows();
		}
	}, [buildingElements]);

	// API FUNCTIONS
	const fetchData = async () => {
		setIsLoading(true);
		try {
			const res = await getAPICall(instance, accounts[0], "/api/coreRepairScopes/buildingElements/all");
			setBuildingElements(res.data);
		} catch (err) {
			console.error("Error fetching building elements:", err);
		}
		setIsLoading(false);
	};

	// HELPER FUNCTIONS
	const buildColumns = () => {
		setColumns([
			{
				accessorKey: "building_type",
				header: () => <span>Building Type</span>,
				label: "Building Type",
				cell: (props) => (
					<span>{props.getValue() ?? notApplicable}</span>
				),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "edit",
				header: "",
				label: "Edit",
				cell: ({ row }) => (
					<IconButton
						onClick={() => onEditClick(row.original.record)}
						sx={{ justifyContent: "flex-end" }}
						disableRipple={true}
					>
						<EditIcon fontSize="small" />
					</IconButton>
				),
				enableSorting: false,
				enableColumnFilter: false,
			},
		]);
	};

	const buildRows = () => {
		setRows(
			buildingElements.map((el) => ({
				id: el.id,
				building_type: el.building_type,
				edit: "",
				record: el,
			}))
		);
	};

	// Dialogs
	const onAddClick = () => {
		setEditing(false);
		setRecordToEdit(null);
		setEditAddOpen(true);
	};

	const onEditClick = (rec) => {
		setEditing(true);
		setRecordToEdit(rec);
		setEditAddOpen(true);
	};

	const onDialogClose = (refresh) => {
		setEditAddOpen(false);
		setRecordToEdit(null);
		setEditing(false);
		if (refresh) fetchData();
	};

	// RENDER
	return (
		<div className="BuildingElements">
			<div className="buildingElementsHeader">
				<div className="buildingElementsHeaderTitle">
					Building Elements
					<div className="buildingElementsHeaderButtons">
						<Button
							variant="contained"
							color="primary"
							onClick={onAddClick}
							disabled={isLoading}
						>
							New Record
						</Button>
					</div>
				</div>
			</div>
			{isLoading && (
				<div className="loadingDiv">
					<CircularProgress color="primary" />
				</div>
			)}
			{!isLoading && (
				<div className="tableContainerDiv">
					{buildingElements.length === 0 ? (
						<J2NoResultsFound />
					) : (
						<TanStackTable
							columnData={columns}
							rowData={rows}
							defaultSort={[{ id: "building_type", desc: false }]}
							showSearch={true}
						/>
					)}
				</div>
			)}
			<BuildingElementsEditAdd
				open={editAddOpen}
				editing={editing}
				recordToEdit={recordToEdit}
				onClose={onDialogClose}
			/>
		</div>
	);
};

export default BuildingElements;
